// Direction filter for facility-scoped analyses.
//
// "Inbound" means the receiving_facility_id is in the user's selected
// facility set ("what value did the customer get?"). "Outbound" means
// the sending_facility_id is in the set ("what referrals did the
// customer generate?"). "Either" matches if EITHER side is in the set
// ("any activity touching the customer").
//
// Only meaningful for data types that carry both sender and receiver
// facility IDs - referrals, vitalrounds, and group-referrals. Single-
// facility data types (messages, logins, studies, study-details, faxes,
// groups) don't have a direction; their filter is just "is this row's
// one facility ID in the selected set?", regardless of the toggle.

export const DIRECTION_INBOUND = 'inbound';
export const DIRECTION_OUTBOUND = 'outbound';
export const DIRECTION_EITHER = 'either';

export const DEFAULT_DIRECTION = DIRECTION_INBOUND;

export const DIRECTION_OPTIONS = [
  {
    value: DIRECTION_INBOUND,
    label: 'Inbound only',
    short: 'Inbound',
    description:
      'Match referrals received by the selected facilities (the customer-value view).',
  },
  {
    value: DIRECTION_OUTBOUND,
    label: 'Outbound only',
    short: 'Outbound',
    description:
      'Match referrals sent FROM the selected facilities (the referral-generation view).',
  },
  {
    value: DIRECTION_EITHER,
    label: 'Any direction',
    short: 'Any',
    description:
      'Match referrals touching the selected facilities on EITHER side (broadest catchment).',
  },
];

// Data types known to carry both sender and receiver facility IDs.
// Anything not in this set is treated as single-facility for filtering
// purposes (the direction toggle has no effect on those rows).
const DIRECTIONAL_DATA_TYPES = new Set([
  'referrals',
  'vitalrounds',
  'group-referrals',
]);

export function dataTypeHasDirection(dataType) {
  return DIRECTIONAL_DATA_TYPES.has(dataType);
}

// Does the page's current selection contain at least one directional
// data type? Used to decide whether to show the toggle in the strip
// (Data Trends has multi-select; the toggle stays visible whenever
// AT LEAST ONE directional type is checked).
export function anyDirectional(types) {
  if (!types) return false;
  if (types instanceof Set) {
    for (const t of types) if (DIRECTIONAL_DATA_TYPES.has(t)) return true;
    return false;
  }
  return Array.from(types).some(t => DIRECTIONAL_DATA_TYPES.has(t));
}

// Returns the user's persisted direction preference, defaulting to
// inbound. Persistence is per-browser so a CS rep who lives in the
// outbound view doesn't have to re-pick after every reload.
export function loadDirectionPreference() {
  try {
    const v = localStorage.getItem('vitaldata.directionPreference');
    if (v === DIRECTION_INBOUND || v === DIRECTION_OUTBOUND || v === DIRECTION_EITHER) {
      return v;
    }
  } catch { /* localStorage unavailable - fall back to default */ }
  return DEFAULT_DIRECTION;
}

export function saveDirectionPreference(direction) {
  try { localStorage.setItem('vitaldata.directionPreference', direction); }
  catch { /* localStorage unavailable - ignore */ }
}

// Coerce an unknown value to a stringified facility ID, matching the
// "round-then-string" form the rest of the app uses (handles "15" /
// "15.0" / 15 / 15.0 uniformly). Returns null when not coercible.
function asFacilityIdString(raw) {
  if (raw == null || raw === '') return null;
  const n = parseFloat(String(raw));
  if (!Number.isFinite(n)) return null;
  return Math.round(n).toString();
}

// Saved filters and resolve-customer responses occasionally store
// facility IDs in raw VE form ("15.0") rather than canonical ("15").
// Check both shapes so the toggle works regardless of which path
// populated `facIds`.
function facIdsHas(facIds, canonical) {
  if (!canonical) return false;
  return facIds.has(canonical) || facIds.has(`${canonical}.0`);
}

// Core matcher used by every page's filter loop. Replaces the inline
// `Boolean(recvStr && facIds.has(recvStr))` checks scattered through
// the codebase.
//
// `rowData` is the inner record payload (record.data || record.Data).
// `dataType` is the rpt_* type the row came from.
// `facIds` is a Set of canonical facility-ID strings.
// `direction` is one of DIRECTION_*; ignored for non-directional types.
//
// Returns true when the row matches the facility filter under the
// requested direction. When facIds is empty, callers should bypass
// this function entirely (no facility filter active).
export function rowMatchesFacilityScope(rowData, dataType, facIds, direction) {
  if (!facIds || facIds.size === 0) return true;
  if (!rowData) return false;

  const directional = dataTypeHasDirection(dataType);

  if (!directional) {
    // Single-facility data type: just check the row's facility_id
    // (with fallback to receiving_facility_id for the cases where the
    // backend stamps both). Direction toggle doesn't apply.
    const fid = asFacilityIdString(
      rowData.facility_id ?? rowData.receiving_facility_id
    );
    return facIdsHas(facIds, fid);
  }

  const recv = asFacilityIdString(rowData.receiving_facility_id);
  const send = asFacilityIdString(rowData.sending_facility_id);

  if (direction === DIRECTION_OUTBOUND) {
    return facIdsHas(facIds, send);
  }
  if (direction === DIRECTION_EITHER) {
    return facIdsHas(facIds, recv) || facIdsHas(facIds, send);
  }
  // Inbound (default).
  return facIdsHas(facIds, recv);
}

// Short tag for the records-info bar / chip label so every page reads
// the same. e.g. "(inbound only)", "(outbound only)", "(any direction)".
export function directionShortLabel(direction) {
  const opt = DIRECTION_OPTIONS.find(o => o.value === direction);
  return opt ? opt.label.toLowerCase() : 'inbound only';
}
